// Import dependencies
import { unsafeCSS, css, html, LitElement } from 'lit';
import { customElement, query, property } from 'lit/decorators.js';
import { repeat } from 'lit/directives/repeat.js';
import { state } from 'lit/decorators.js';
import { until } from 'lit/directives/until.js';
import { isEmpty, isDefined, getRandomInteger, addParametersToURL, getURLParameter, debounce, wpRestCall } from '@utilities';
const TomSelect = require( 'tom-select/dist/js/tom-select.complete.js' );

// Import style for web component
const styles = require( './web-component-style.scss' );

// eslint-disable-next-line require-jsdoc
@customElement( 'lfm-faq' )
export class FAQ extends LitElement {
    // Define style
    static styles = css`${ unsafeCSS( styles ) }`;

    // Properties
    // dormant: If TRUE, we won't request items on page load to get the first page
    // This means that in order to get results `.search` must be invoked
	@property({ type: Boolean }) dormant = false;

    // Create a reactive property to save the results
    @state() items = [];

    // Non-reactive property
    _isFirstLoad = true;
    itemsPerPage = 20;
    // currentPage: The UI loads empty, so the current page on page load is 0.
    // The property `dormant` will decide whether we will do a request on page load automatically to get the first page or not.
    currentPage = 0;
    reachedLastPage = false;

    @query( '#search-field' ) $searchField;
    @query( '#tags-select' ) $tagsSelect;
    // Get the sentinel for the Intersection Observer API
    // We'll use this to detect that the user reached the end of the page
    @query( '#observer-sentinel' ) $sentinel;

    // eslint-disable-next-line require-jsdoc
    render() {
        return html`
            <div class="faq">
                ${ this.templateSearch }
                ${ this.templateResults }
            </div>
        `;
    }

    /**
     * Renders the tags dropdown
     * Invoked by firstUpdated()
     */
    renderTagsDropdown() {
        // Create the dropdown options
        const options = [];

        try { 
            Object.entries( UOLFM_FAQ.tags ).forEach( ( [ tagSlug, tag ] ) => {
                options.push( {
                    value: tagSlug,
                    text:  tag.name
                } );
            } );
        } catch ( e ) {
            console.warn( `Invalid FAQ tags. Check \`UOLFM_FAQ.tags\`.` );
        }

        new TomSelect(
            this.$tagsSelect,
            {
                plugins: [ 'no_active_items', 'no_backspace_delete', 'remove_button' ],
                // hidePlaceholder: If true, the placeholder will be hidden when the control has one or more items (selected options) and is not focused. 
                hidePlaceholder: true,
                // options: [ { value: (string), text: (string) } ]
                options: options,
                // items: An array of the initial selected values. By default this is populated from the original input element.
                items: this.tagsFilter,
                // render templates
                render: {
                    option: ( data ) => {
                        return `
                            <div>
                                <div class="ts-tag-mask">
                                    <lfm-faq-tag tag-id="${ data.value }"></lfm-faq-tag>
                                </div>
                            </div>
                        `;
                    },

                    item: ( item ) => {
                        // Get tag data
                        const tagData = UOLFM_FAQ.tags[ item.value ];

                        return `
                            <div
                                class="ts-tag-extended"
                                style="background-color: ${ ! isEmpty( tagData ) && ! isEmpty( tagData.color ) ? `${ tagData.color } !important` : '' }"
                            >
                                <div class="ts-tag-mask">
                                    <lfm-faq-tag tag-id="${ item.value }"></lfm-faq-tag>
                                </div>
                            </div>
                        `;
                    }
                }
            }
        );
    }

    /**
     * Saves the search query and tags, and perform a search
     */
    filter() {
        // Get the search query
        const searchQuery = this.$searchField.value;
        
        // Get the slugs of the selected tags
        const selectedTags = [ ...this.$tagsSelect.options ]
            .filter( option => option.selected )
            .map( option => option.value );

        // Add the query to the URL
        addParametersToURL(
            UOLFM_FAQ.urls.faqArchive, // The FAQ archive URL
            {
                search: searchQuery,
                tags: selectedTags
            },
            {
                setAsURL: true,
                refresh: false
            }
        );

        // Reset the current results to trigger a new search
        this.reset();
    }

    /**
     * Gets and saves the results of the next page
     * 
     * @returns {Promise}
     */
    async getNextPage() {
        // Get the page that we have to get
        // If there are results, this will be the next current page once the Promise gets resolved
        const page = this.currentPage + 1;

        const data = {
            per_page: this.itemsPerPage,
            page: page, // on first run, this.currentPage will be 0, so we'll get the results of the first page

            // Add the search query, even if it's empty
            query: this.searchQuery,
            tags: this.tagsFilter
        }

        // Trigger event before doing the REST call
        this.dispatchEvent(
			new CustomEvent( 'lfm-faq/get-results/start', {
				bubbles: false, 
				composed: true,
                detail: {
                    queryAttributes: data
                }
			} )
		);

        return await wpRestCall( {
                url: `${ UOLFM_FAQ.rest.url }/faq-list`,
                nonce: UOLFM_FAQ.rest.nonce,
                keepalive: true,
                data: data
            } )
            .then( response => {
                // Check if there aren't more results
                if ( 
                    // Check if there are not results. This could happen if there are no results at all
                    isEmpty( response.results ) || 
                    // Or if the current page is the last page
                    response.page === response.total_pages
                ) {
                    this.reachedLastPage = true;
                } {
                    // Update the count of the current page
                    this.currentPage = page;
                }

                // Save the results, even if it's an empty array. This will re-render the UI
                // Check if it's the first page 
                this.items = this.currentPage === 1 ? Object.entries( response.results ) : [ ...this.items, ...Object.entries( response.results ) ];

                // Trigger event before updating the URL
                this.dispatchEvent(
                    new CustomEvent( 'lfm-faq/get-results/success', {
                        bubbles: false, 
                        composed: true,
                        detail: {
                            queryAttributes: data,
                            reachedLastPage: this.reachedLastPage,
                            currentPage: this.currentPage,
                            items: this.items
                        }
                    } )
                );

                // Continue
                return response;
            } );
    }

    /**
     * Resets the component: removes all items, and loads the first page again
     */
    reset() {
        this.currentPage = 0;
        this.reachedLastPage = false;
        this.items = []; // This is a reactive property, so it will render the UI again
    }

    // eslint-disable-next-line require-jsdoc
    updated() {
        // Listen the sentinel that's going to be added below
        this.handleSentinel();
    }

    /**
     * Listens to the scroll behavior of the user, and checks if it's time to load more results using the Intersection Observer API. 
     */
    handleSentinel() {
        // Check if we have to remove the previous observer
        if ( isDefined( this.intersectionObserver ) ) {
            this.intersectionObserver.disconnect();
        }

        // Check if there is a sentinel at all
        if ( ! isDefined( this.$sentinel ) ) {
            return;
        }

        // Create the intersection observer
        this.intersectionObserver = new IntersectionObserver(
            ( entries ) => {
                // If the browser is busy while scrolling happens, multiple entries can
                // accumulate between invocations of this callback. As long as any one
                // of the notifications reports the sentinel within the scrolling viewport,
                // we add more content.
                if ( entries.some( entry => entry.intersectionRatio > 0 ) ) {
                    // Check if we already reached the end and do nothing
                    if ( this.reachedLastPage ) {
                        return;
                    }

                    // Load more results
                    this.getNextPage();
                }
            }, 
            {
                rootMargin: '400px',
                threshold: 1.0, // Either a single number or an array of numbers which indicate at what percentage of the target's visibility the observer's callback should be executed. If you only want to detect when visibility passes the 50% mark, you can use a value of 0.5.
            }
        );

        // Observe the sentinel
        this.intersectionObserver.observe( this.$sentinel );
    }

    /**
     * Returns the wrapper that contains the search field and tags filter
     * 
     * @returns {TemplateResult} The search section
     */
    get templateSearch() {
        return html`
            <div class="faq-filters">
                <div class="faq-filters-inner">
                    <h2>${ UOLFM_FAQ.i18n.searchTitle }</h2>

                    <div class="faq-filters-controls">
                        <input
                            id="search-field"
                            class="faq-filters-search-field"

                            type="text"
                            value=${ this.searchQuery }
                            
                            placeholder="${ UOLFM_FAQ.i18n.searchPlaceholder }"

                            @input=${ debounce( () => { this.filter() }, 500 ) }
                        >

                        <div class="faq-filters-tags">
                            <label class="faq-filters-tags-label">${ UOLFM_FAQ.i18n.tagsLabel }</label>

                            <select 
                                multiple
                                placeholder="${ UOLFM_FAQ.i18n.allTags }" 
                                autocomplete="off"

                                id="tags-select"
                                class="faq-filters-tags-field"

                                @change=${ debounce( () => { this.filter() }, 500 ) }
                            ></div>
                        </div>
                </div>
            </div>
        `;
    }

    /**
     * Returns the list with the results, or the placeholders
     * 
     * @returns {TemplateResult} The results section
     */
    get templateResults() {
        // Check if it's the first load, and if we shouldn't trigger a request on load
        if ( this._isFirstLoad && this.dormant ) {
            return html``;
        }

        // Check if we have to get more items
        if ( isEmpty( this.items ) ) {
            // Check if there aren't results
            // For this, use .reachedLastPage, which will be TRUE if the REST call returned no results
            if ( ! this.reachedLastPage ) {
                return until(
                    this.getNextPage(),
                    this.templateLoadingPlaceholder
                );
            }
        }

        // And just show the slot inside this component
        return html`
            <div class="faq-content">
                ${ 
                    ! isEmpty( this.items ) ? 
                    html`
                        <div class="faq-list">
                            ${ repeat(
                                // Get integrations with conditions
                                this.items,
                                // eslint-disable-next-line no-unused-vars
                                ( [ faqID, faq ] ) => faqID,
                                ( [ faqID, faq ] ) => html`
                                    <lfm-faq-item
                                        faq-id="${ faqID }"
                
                                        .question=${ faq.title }
                                        .permalink=${ faq.permalink }
                                        .tags=${ faq.tags }
                                        .editURL=${ faq.edit_url }
                                    ></lfm-faq-item>
                                `
                            ) }
                        </div>
                    ` : html``
                }

                ${ ! this.reachedLastPage ? 
                    // The sentinel will be used to detect when we have to load more results
                    html`<div id="observer-sentinel"></div>` : 
                    // But if this is the last page, don't add the sentinel. Render something else instead
                    this.templateNoMoreResults
                }
            </div>
        `;
    }

    /**
     * The content displayed while the FAQ content loads
     * 
     * @returns {TemplateResult} The "no [more] results" section
     */
    get templateNoMoreResults() {
        return html`
            <div class="faq-need-help">
                <div class="faq-need-help-heading">
                    ${ UOLFM_FAQ.i18n.noMoreResults }
                </div>

                <a 
                    href="${ UOLFM_FAQ.urls.help }" 
                    target="_blank"
                    class="faq-need-help-button"
                >
                    ${ UOLFM_FAQ.i18n.submitTicketBtn }
                </a>
            </div>
        `;
    }

    /**
     * The content displayed while the FAQ content loads
     * 
     * @returns {TemplateResult} The loading placeholder
     */
    get templateLoadingPlaceholder() {
        // Create an array where we're going to save our fake items
        // It will have arrays where each element represents a word, and the value represents its length in pixels
        const fakeItems = [];

        // Add 10 fake items
        for ( let i = 0; i < this.itemsPerPage; i++ ) {
            const words = [];
            const numberOfWords = getRandomInteger( 3, 6 );

            // Create between 8 to 20 words per fake item
            for ( let j = 0; j < numberOfWords; j++ ) {
                // Get a random length
                words.push( getRandomInteger( 30, 150 ) );
            }

            fakeItems.push( words );
        }

        return html`
            <div class="faq-content">
                ${ repeat(
                    // Get integrations with conditions
                    Object.entries( fakeItems ),
                    // eslint-disable-next-line no-unused-vars
                    ( [ index, words ] ) => index,
                    // eslint-disable-next-line no-unused-vars
                    ( [ index, words ] ) => html`
                        <div class="faq-item-placeholder">
                            ${
                                repeat(
                                    Object.entries( words ),
                                    // eslint-disable-next-line no-unused-vars
                                    ( [ index, length ] ) => index,
                                    // eslint-disable-next-line no-unused-vars
                                    ( [ index, length ] ) => html`
                                        <lfm-faq-placeholder width="${ length }px"></lfm-faq-placeholder>
                                    `
                                )
                            }
                        </div>
                    `
                ) }
            </div>
        `;
    }

    /**
     * Returns the current search query
     * 
     * @returns {String} The search query
     */
    get searchQuery() {
        // Get search parameter from the URL
        const query = getURLParameter( 'search' );

        return ! isEmpty( query ) ? query : '';
    }

    /**
     * Returns the selected filters
     */
    get tagsFilter() {
        let tags = [];

        // Check if we should add the tags
        const tagsFromURL = getURLParameter( 'tags' );

        if ( ! isEmpty( tagsFromURL ) ) {
            tags = tagsFromURL.split( ',' );
        }

        // Get all tag IDs
        const allTags = Object.keys( UOLFM_FAQ.tags );

        // Check if the tag is valid
        tags = tags.filter( tagID => allTags.includes( tagID ) );

        return tags;
    }

    // eslint-disable-next-line require-jsdoc
    firstUpdated() {
        this._isFirstLoad = false;

        // Init the library that renders the tags multiselect
        this.renderTagsDropdown();
    }
}
